import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout, fetchTags } from "../../redux/action";
import { motion, AnimatePresence } from "framer-motion";
import AnimatedLoader from "../../motionKit/AnimatedLoader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faTag,
  faEllipsisV,
  faEdit,
  faTrash,
  faChevronDown,
  faUserGear,
  faRightFromBracket,
} from "@fortawesome/free-solid-svg-icons";
import { useSideBarContext } from "../../Providers/SideBarProvider";
import "./Admin.css";
import { showSwal } from "../../Helper/ShowAlert";
import { setupSSEConnection } from "../../Api";
import { useDarkMode } from "../../context/DarkModeContext";
import TagEditModal from "../../components/shared/TagEditModal";

const isValidReactComponent = (input) => {
  if (typeof input === "function") return true;
  if (typeof input === "object" && input !== null && "$$typeof" in input) return true;
  return false;
};

const isImagePath = (input) =>
  typeof input === "string" &&
  (input.endsWith(".svg") || input.endsWith(".png") || input.endsWith(".jpg"));

/** Onboarding tour anchors — see walkthrough/tours/onboardingTour.js */
const dataTourForSidebarItem = (item) => {
  if (!item?.name) return undefined;
  if (item.name === "Dashboard") return "sidebar-dashboard";
  if (item.name === "Manual Queries") return "sidebar-queries";
  if (item.name === "Quotes") return "sidebar-quotes";
  return undefined;
};

const renderIcon = (Icon, isActive) => {
  if (!Icon) return null;
  if (isValidReactComponent(Icon)) {
    return typeof Icon === "function"
      ? <Icon color={isActive ? "#FFFFFF" : "#94A3B8"} />
      : Icon;
  }
  if (isImagePath(Icon)) {
    return (
      <img
        src={Icon}
        alt=""
        className="h-4 w-4"
        style={{
          filter: isActive
            ? "brightness(0) invert(1)"
            : "brightness(0) saturate(100%) invert(64%) sepia(10%) saturate(500%) hue-rotate(182deg) brightness(92%) contrast(88%)",
        }}
      />
    );
  }
  return null;
};

// Hoisted to MODULE scope (and memoized) deliberately: defined inside SideBar
// it became a new component type on every render, so React remounted every
// item on each navigation — the whole list re-ran its entrance animation and
// the active pill couldn't travel. Stable type = items persist, the cascade
// runs once on true mount, and the pill SLIDES across the other options to
// its target via the shared layoutId.
// `order` drives that one-time mount cascade (delay = order * 35ms).
const NavItem = React.memo(function NavItem({
  item,
  isActive,
  indented = false,
  order = 0,
  collapsed,
  showContents,
  onGo,
}) {
  return (
    <motion.li
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.28, delay: order * 0.035, ease: [0.22, 1, 0.36, 1] }}
    >
      {showContents ? (
        <button
          type="button"
          title={item.name}
          aria-label={item.name}
          data-tour={dataTourForSidebarItem(item)}
          onClick={() => onGo(item)}
          className={`
            group relative flex w-full items-center gap-3 rounded-xl px-3 py-2 text-[13px] font-medium
            transition-colors duration-150 outline-none
            ${collapsed ? "justify-center px-0" : ""}
            ${indented && !collapsed ? "pl-9" : ""}
            ${isActive
              ? "text-white"
              : "text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-[oklch(0.32_0.02_264/0.5)] dark:hover:text-slate-200"
            }
          `}
        >
          {/* The active pill SLIDES between items (shared layout animation). */}
          {isActive && (
            <motion.span
              layoutId="fk-nav-pill"
              className="absolute inset-0 rounded-xl bg-fk-accent shadow-sm"
              transition={{ type: "spring", stiffness: 480, damping: 38 }}
              aria-hidden
            />
          )}
          <span
            className={`relative z-10 flex h-5 w-5 shrink-0 items-center justify-center transition-transform duration-200 group-hover:-rotate-6 group-hover:scale-110 ${
              isActive ? "text-white" : "text-slate-400 dark:text-slate-500"
            }`}
          >
            {renderIcon(item.icon, isActive)}
          </span>
          {!collapsed && (
            <span className="relative z-10 truncate">{item.name}</span>
          )}
        </button>
      ) : (
        <span className="text-slate-400 dark:text-slate-600 text-sm px-3 py-2">{item.name}</span>
      )}
    </motion.li>
  );
});

function SideBar({ sideBarItems }) {
  const { isOpen, toggleSidebar, activePage, setActivePage } =
    useSideBarContext();
  const collapsed = !isOpen;
  const { isDarkMode } = useDarkMode();
  const loginToken = useSelector((state) => state.reducer.userToken);
  const dispatch = useDispatch();
  const sideBarRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const CompanyExist = useSelector((state) => state.reducer.userLoggedIn?.companyExist);
  const showSideBarContents = CompanyExist == undefined ? true : CompanyExist;
  const userLoggedIn = useSelector((state) => state.reducer.userLoggedIn);
  const tags = useSelector((state) => state.reducer.tags?.list || []);

  const [tagMenuOpen, setTagMenuOpen] = useState(null);
  const [editTagModal, setEditTagModal] = useState(null);
  const [editModalMode, setEditModalMode] = useState("edit");
  const [openGroups, setOpenGroups] = useState({});
  const [isTagsOpen, setIsTagsOpen] = useState(true);

  const isGrouped = sideBarItems && sideBarItems.groups !== undefined;

  // Longest-prefix active detection: an item is active if the current pathname
  // equals its path OR starts with its path + "/". Among all matching items,
  // only the one with the LONGEST path wins — so nested detail routes like
  // /:role/newQuotePage/:id keep the closest section (e.g. Quotes) highlighted.
  // Computed once here; each NavItem just compares its path to the winner.
  const allNavItems = React.useMemo(() => {
    if (!sideBarItems) return [];
    if (isGrouped) {
      return [
        ...(sideBarItems.groups || []).flatMap((g) => g.items || []),
        ...(sideBarItems.standalone || []),
      ].filter(Boolean);
    }
    return Object.values(sideBarItems).filter(Boolean);
  }, [sideBarItems, isGrouped]);

  const allNavPaths = React.useMemo(
    () => allNavItems.map((it) => it?.path).filter(Boolean),
    [allNavItems]
  );

  // Nested detail routes (a quote workspace, a manual-query detail, the
  // document generator) have no nav entry of their own — map them to the parent
  // section so it stays highlighted while the operator is deep inside it.
  const DETAIL_ROUTE_PARENTS = [
    { match: "/newQuotePage/", name: "Quotes" },
    { match: "/generate-documents", name: "Quotes" },
    { match: "/manual-query/", name: "Manual Queries" },
  ];

  const activePath = React.useMemo(() => {
    const matches = allNavPaths.filter(
      (p) => location.pathname === p || location.pathname.startsWith(p + "/")
    );
    if (matches.length > 0) {
      return matches.sort((a, b) => b.length - a.length)[0];
    }
    const parent = DETAIL_ROUTE_PARENTS.find((d) =>
      location.pathname.includes(d.match)
    );
    if (parent) {
      const item = allNavItems.find((it) => it?.name === parent.name);
      if (item?.path) return item.path;
    }
    return null;
  }, [allNavPaths, allNavItems, location.pathname]);

  const profileItem = sideBarItems?.standalone?.find((i) => i?.name === "My Profile");
  const logoutItem = sideBarItems?.standalone?.find((i) => i?.name === "Logout");
  const userInitial = (userLoggedIn?.name || userLoggedIn?.email || "U").trim().slice(0, 1).toUpperCase();

  useEffect(() => {
    if (userLoggedIn?.role === "operator" && loginToken) {
      dispatch(fetchTags(loginToken, userLoggedIn.role));
    }
  }, [userLoggedIn?.role, loginToken, dispatch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tagMenuOpen && !event.target.closest('.relative')) {
        setTagMenuOpen(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [tagMenuOpen]);

  useEffect(() => {
    if (isGrouped && sideBarItems?.groups?.length) {
      setOpenGroups((prev) => {
        const next = { ...prev };
        sideBarItems.groups.forEach((group) => {
          if (next[group.title] === undefined) {
            next[group.title] = true;
          }
        });
        return next;
      });
    }
  }, [isGrouped, sideBarItems]);

  const go = (item) => {
    if (!item?.path) return;
    if (item.name === "Logout") {
      localStorage.setItem("myProcess", "logout");
      dispatch(logout());
      showSwal("Logged Out", "", 200, () => navigate("/loginPage"));
      return;
    }
    setActivePage(item.name);
    navigate(item.path);
  };

  return (
    <>
      {/* Mobile drawer backdrop — below lg only, and only while the drawer is open. */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[55] bg-slate-900/40 backdrop-blur-[1px] lg:hidden"
          onClick={toggleSidebar}
          aria-hidden
        />
      )}
      <aside
      id="sideBar"
      ref={sideBarRef}
      className={`
        relative z-30 flex min-h-0 flex-col self-stretch border-r border-slate-200/70 bg-white/55
        dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.267_0.037_268/0.38)]
        w-sidebar ${collapsed ? "lg:w-sidebar-collapsed" : "lg:w-sidebar"}
        max-lg:fixed max-lg:inset-y-0 max-lg:left-0 max-lg:z-[60] max-lg:shadow-2xl lg:static
        -translate-x-full lg:translate-x-0 ${isOpen ? "max-lg:translate-x-0" : ""}
        transition-[width,transform] duration-200 ease-out
      `}
    >
      {/* Collapse toggle */}
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggleSidebar();
        }}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        className="absolute -right-1 top-5 z-50 flex h-6 w-6 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-400 shadow-sm transition-all hover:text-slate-600 hover:shadow dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-500 dark:hover:text-slate-300"
      >
        <FontAwesomeIcon
          icon={faArrowLeft}
          className={`pointer-events-none text-[10px] transition-transform duration-200 ${collapsed ? "rotate-180" : ""}`}
        />
      </button>

      {/* Logo / brand area — the live FK mark (plane orbiting the tile) IS the
          logo now; the wordmark sits beside it when expanded. */}
      <div className={`flex items-center gap-2 h-[60px] shrink-0 border-b border-slate-100 dark:border-[oklch(0.3_0.02_264/0.6)] ${collapsed ? "justify-center pr-4" : "px-4"}`}>
        {/* The live FK mark — plane orbiting the tile with the bold beaded
            ring (user-approved; do not swap for the calm variant). A soft blue
            aura breathes BEHIND it — kept gentle so it glows rather than washing
            the white sidebar; the sacred orbit mark itself is untouched. */}
        <span className="relative inline-flex shrink-0 items-center justify-center" style={{ width: 56, height: 56 }}>
          <span className="fk-aura-halo pointer-events-none absolute -z-10" style={{ inset: "-16%", borderRadius: "50%", background: "radial-gradient(circle, rgba(37,99,235,0.26), rgba(37,99,235,0.07) 55%, transparent 72%)", filter: "blur(5px)" }} aria-hidden />
          <AnimatedLoader variant="route" size={56} label="FreightKit" extraProps={{ logo: true }} />
        </span>
        {!collapsed && (
          <span className="text-[15px] font-semibold text-slate-900 dark:text-white tracking-tight">
            FreightKit
          </span>
        )}
      </div>

      {/* Navigation — data-tour for onboarding walkthrough */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-4 px-3" data-tour="sidebar-nav">
        <ul className="space-y-0.5">
          {isGrouped ? (
            <>
              {/* Dashboard (standalone) */}
              {sideBarItems.standalone && sideBarItems.standalone
                .filter(item => item.name === "Dashboard")
                .map((item, index) => {
                  const isActive = item.path === activePath;
                  return <NavItem key={`dash-${index}`} item={item} isActive={isActive} order={0} collapsed={collapsed} showContents={showSideBarContents} onGo={go} />;
                })}

              {/* Groups */}
              {sideBarItems.groups && sideBarItems.groups.map((group, groupIndex) => {
                const isGroupOpen = openGroups[group.title] !== false;
                return (
                  <li key={`group-${groupIndex}`} className="mt-5">
                    {!collapsed && (
                      <button
                        type="button"
                        onClick={() =>
                          setOpenGroups((prev) => ({
                            ...prev,
                            [group.title]: !isGroupOpen,
                          }))
                        }
                        className="flex w-full items-center justify-between px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-400 transition-colors"
                      >
                        <span>{group.title}</span>
                        <FontAwesomeIcon
                          icon={faChevronDown}
                          className={`text-[9px] transition-transform duration-200 ${isGroupOpen ? "rotate-0" : "-rotate-90"}`}
                        />
                      </button>
                    )}

                    <AnimatePresence initial={false}>
                      {(isGroupOpen || collapsed) && (
                        <motion.ul
                          key="group-items"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                          className="mt-1 space-y-0.5 overflow-hidden"
                        >
                          {group.items.map((item, itemIndex) => {
                            const isActive = item.path === activePath;
                            return <NavItem key={`g${groupIndex}-i${itemIndex}`} item={item} isActive={isActive} order={1 + groupIndex * 5 + itemIndex} collapsed={collapsed} showContents={showSideBarContents} onGo={go} />;
                          })}
                        </motion.ul>
                      )}
                    </AnimatePresence>
                  </li>
                );
              })}

              {/* Tags for operators */}
              {userLoggedIn?.role === "operator" && (
                <li className="mt-5">
                  {showSideBarContents && !collapsed ? (
                    <button
                      type="button"
                      onClick={() => setIsTagsOpen((prev) => !prev)}
                      className="flex w-full items-center gap-2 px-3 py-2 text-[13px] font-medium text-slate-600 hover:bg-slate-100 rounded-xl transition-colors dark:text-slate-400 dark:hover:bg-[oklch(0.32_0.02_264/0.5)]"
                    >
                      <FontAwesomeIcon icon={faTag} className="text-violet-500 text-xs" />
                      <span>My Tags</span>
                      <FontAwesomeIcon
                        icon={faChevronDown}
                        className={`ml-auto text-[9px] text-slate-400 transition-transform duration-200 ${isTagsOpen ? "rotate-0" : "-rotate-90"}`}
                      />
                    </button>
                  ) : null}

                  {isTagsOpen && (
                    <ul className="mt-1 space-y-0.5">
                      {tags.length === 0 ? (
                        <li className="px-3 py-2 text-xs text-slate-400 dark:text-slate-500">
                          No tags yet
                        </li>
                      ) : (
                        tags.map((tag) => {
                          const isActive = location.pathname === `/operator/tags/${tag._id}`;
                          return (
                            <li key={tag._id} className="relative group">
                              <div className="flex items-center">
                                <Link
                                  to={`/operator/tags/${tag._id}`}
                                  className="no-underline flex-1"
                                  onClick={() => setActivePage(`Tag: ${tag.name}`)}
                                >
                                  <div className={`
                                    flex items-center gap-2.5 rounded-xl px-3 py-2 text-[13px] font-medium transition-all duration-150
                                    ${collapsed ? "justify-center" : "pl-9"}
                                    ${isActive
                                      ? "bg-fk-accent text-white shadow-sm"
                                      : "text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-[oklch(0.32_0.02_264/0.5)]"
                                    }
                                  `}>
                                    <div
                                      className="w-2.5 h-2.5 rounded-full shrink-0"
                                      style={{ backgroundColor: tag.color || "#6366F1" }}
                                    />
                                    {!collapsed && <span className="truncate">{tag.name}</span>}
                                  </div>
                                </Link>
                                {!collapsed && (
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setTagMenuOpen(tagMenuOpen === tag._id ? null : tag._id);
                                    }}
                                    className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-[oklch(0.32_0.02_264/0.5)] dark:hover:text-slate-300 transition-all"
                                    aria-label="Tag options"
                                  >
                                    <FontAwesomeIcon icon={faEllipsisV} className="w-2.5 h-2.5" />
                                  </button>
                                )}
                                {tagMenuOpen === tag._id && (
                                  <div className="absolute right-0 top-full mt-1 z-50 w-36 rounded-xl bg-white dark:bg-[oklch(0.26_0.028_264)] border border-slate-200 dark:border-[oklch(0.33_0.022_264)] shadow-elevated overflow-hidden">
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setEditTagModal(tag);
                                        setEditModalMode("edit");
                                        setTagMenuOpen(null);
                                      }}
                                      className="w-full text-left px-3 py-2 text-[13px] flex items-center gap-2 text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-[oklch(0.32_0.02_264/0.5)] transition-colors"
                                    >
                                      <FontAwesomeIcon icon={faEdit} className="w-3 h-3" />
                                      Rename
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setEditTagModal(tag);
                                        setEditModalMode("delete");
                                        setTagMenuOpen(null);
                                      }}
                                      className="w-full text-left px-3 py-2 text-[13px] flex items-center gap-2 text-red-500 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10 transition-colors"
                                    >
                                      <FontAwesomeIcon icon={faTrash} className="w-3 h-3" />
                                      Delete
                                    </button>
                                  </div>
                                )}
                              </div>
                            </li>
                          );
                        })
                      )}
                    </ul>
                  )}
                </li>
              )}

              {/* Other standalone items */}
              {sideBarItems.standalone && sideBarItems.standalone
                .filter(item => item.name !== "Dashboard" && item.name !== "My Profile" && item.name !== "Logout")
                .map((item, index) => {
                  const isActive = item.path === activePath;
                  return <NavItem key={`standalone-${index}`} item={item} isActive={isActive} order={12 + index} collapsed={collapsed} showContents={showSideBarContents} onGo={go} />;
                })}
            </>
          ) : (
            <>
              {Object.values(sideBarItems)
                .filter((item) => item?.name !== "My Profile" && item?.name !== "Logout")
                .map((item, index) => {
                  const isActive = item.path === activePath;
                  return <NavItem key={index} item={item} isActive={isActive} order={index} collapsed={collapsed} showContents={showSideBarContents} onGo={go} />;
                })}

              {/* Tags for operators (flat structure) */}
              {userLoggedIn?.role === "operator" && (
                <li className="mt-5">
                  {showSideBarContents && !collapsed ? (
                    <button
                      type="button"
                      onClick={() => setIsTagsOpen((prev) => !prev)}
                      className="flex w-full items-center gap-2 px-3 py-2 text-[13px] font-medium text-slate-600 hover:bg-slate-100 rounded-xl transition-colors dark:text-slate-400 dark:hover:bg-[oklch(0.32_0.02_264/0.5)]"
                    >
                      <FontAwesomeIcon icon={faTag} className="text-violet-500 text-xs" />
                      <span>My Tags</span>
                    </button>
                  ) : null}

                  {isTagsOpen && (
                    <ul className="mt-1 space-y-0.5">
                      {tags.length === 0 ? (
                        <li className="px-3 py-2 text-xs text-slate-400 dark:text-slate-500">
                          No tags yet
                        </li>
                      ) : (
                        tags.map((tag) => {
                          const isActive = location.pathname === `/operator/tags/${tag._id}`;
                          return (
                            <li key={tag._id} className="relative group">
                              <div className="flex items-center">
                                <Link
                                  to={`/operator/tags/${tag._id}`}
                                  className="no-underline flex-1"
                                  onClick={() => setActivePage(`Tag: ${tag.name}`)}
                                >
                                  <div className={`
                                    flex items-center gap-2.5 rounded-xl px-3 py-2 text-[13px] font-medium transition-all duration-150
                                    ${isActive
                                      ? "bg-fk-accent text-white shadow-sm"
                                      : "text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-[oklch(0.32_0.02_264/0.5)]"
                                    }
                                  `}>
                                    <div
                                      className="w-2.5 h-2.5 rounded-full shrink-0"
                                      style={{ backgroundColor: tag.color || "#6366F1" }}
                                    />
                                    {!collapsed && <span className="truncate">{tag.name}</span>}
                                  </div>
                                </Link>
                                {!collapsed && (
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setTagMenuOpen(tagMenuOpen === tag._id ? null : tag._id);
                                    }}
                                    className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-[oklch(0.32_0.02_264/0.5)] dark:hover:text-slate-300 transition-all"
                                    aria-label="Tag options"
                                  >
                                    <FontAwesomeIcon icon={faEllipsisV} className="w-2.5 h-2.5" />
                                  </button>
                                )}
                                {tagMenuOpen === tag._id && (
                                  <div className="absolute right-0 top-full mt-1 z-50 w-36 rounded-xl bg-white dark:bg-[oklch(0.26_0.028_264)] border border-slate-200 dark:border-[oklch(0.33_0.022_264)] shadow-elevated overflow-hidden">
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setEditTagModal(tag);
                                        setEditModalMode("edit");
                                        setTagMenuOpen(null);
                                      }}
                                      className="w-full text-left px-3 py-2 text-[13px] flex items-center gap-2 text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-[oklch(0.32_0.02_264/0.5)] transition-colors"
                                    >
                                      <FontAwesomeIcon icon={faEdit} className="w-3 h-3" />
                                      Rename
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setEditTagModal(tag);
                                        setEditModalMode("delete");
                                        setTagMenuOpen(null);
                                      }}
                                      className="w-full text-left px-3 py-2 text-[13px] flex items-center gap-2 text-red-500 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10 transition-colors"
                                    >
                                      <FontAwesomeIcon icon={faTrash} className="w-3 h-3" />
                                      Delete
                                    </button>
                                  </div>
                                )}
                              </div>
                            </li>
                          );
                        })
                      )}
                    </ul>
                  )}
                </li>
              )}
            </>
          )}
        </ul>
      </nav>

      {/* Bottom user card */}
      {(profileItem || logoutItem) && showSideBarContents && (
        <div className={`shrink-0 border-t border-slate-100 dark:border-[oklch(0.3_0.02_264/0.6)] p-3 ${collapsed ? "flex flex-col items-center gap-2" : ""}`}>
          {!collapsed ? (
            <div className="rounded-xl bg-slate-50 dark:bg-white/[0.04] p-3">
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-fk-accent/10 text-xs font-semibold text-fk-accent">
                  {userInitial}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13px] font-medium text-slate-900 dark:text-white">
                    {userLoggedIn?.name || "Account"}
                  </div>
                  <div className="truncate text-[11px] text-slate-400 dark:text-slate-500">
                    {userLoggedIn?.email || ""}
                  </div>
                </div>
              </div>
              <div className="mt-2.5 grid grid-cols-2 gap-2">
                {profileItem && (
                  <button
                    type="button"
                    onClick={() => go(profileItem)}
                    className="h-8 rounded-lg border border-slate-200 bg-white text-[12px] font-medium text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-slate-900 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-300 dark:hover:bg-[oklch(0.3_0.024_264)]"
                  >
                    Settings
                  </button>
                )}
                {logoutItem && (
                  <button
                    type="button"
                    onClick={() => go(logoutItem)}
                    className="h-8 rounded-lg border border-red-200 bg-red-50 text-[12px] font-medium text-red-600 shadow-sm transition hover:bg-red-100 dark:border-red-900/40 dark:bg-red-500/10 dark:text-red-400 dark:hover:bg-red-500/15"
                  >
                    Log out
                  </button>
                )}
              </div>
            </div>
          ) : (
            <>
              {profileItem && (
                <button
                  type="button"
                  onClick={() => go(profileItem)}
                  aria-label="Settings"
                  title="Settings"
                  className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-400 shadow-sm transition hover:text-slate-600 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-500"
                >
                  <FontAwesomeIcon icon={faUserGear} className="text-xs" />
                </button>
              )}
              {logoutItem && (
                <button
                  type="button"
                  onClick={() => go(logoutItem)}
                  aria-label="Log out"
                  title="Log out"
                  className="flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 bg-red-50 text-red-500 shadow-sm transition hover:bg-red-100 dark:border-red-900/40 dark:bg-red-500/10 dark:text-red-400"
                >
                  <FontAwesomeIcon icon={faRightFromBracket} className="text-xs" />
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* Tag Edit/Delete Modal */}
      {editTagModal && (
        <TagEditModal
          isOpen={!!editTagModal}
          onClose={() => {
            setEditTagModal(null);
            setEditModalMode("edit");
          }}
          tag={editTagModal}
          mode={editModalMode}
        />
      )}
    </aside>
    </>
  );
}

export default SideBar;
